import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Shield, AlertTriangle, CheckCircle, XCircle, ChevronDown, ChevronUp,
  Clock, Activity, TrendingUp, TrendingDown, Lock, Unlock, Eye,
  Search, RefreshCw, ShieldCheck, ShieldAlert, ArrowRight, Hash,
  AlertCircle, Zap, Filter, Calendar, ChevronRight, Loader,
} from 'lucide-react';
import * as api from '../services/api';
import TimelineScrubber from './TimelineScrubber';
import { publishedBand, bandInk } from '../services/trustBands';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const DECISION_META = {
  ALLOWED:   { color: '#16a34a', bg: '#f0fdf4', icon: CheckCircle,    label: 'Allowed' },
  BLOCKED:   { color: '#dc2626', bg: '#fef2f2', icon: XCircle,        label: 'Blocked' },
  WARNED:    { color: '#d97706', bg: '#fffbeb', icon: AlertTriangle,   label: 'Warned' },
  AUDIT:     { color: '#6366f1', bg: '#eef2ff', icon: Eye,            label: 'Audit' },
  REDACTED:  { color: '#8b5cf6', bg: '#f5f3ff', icon: ShieldAlert,    label: 'Redacted' },
};

const AUTONOMY_META = {
  SELF_AUTONOMOUS: { color: '#0d9488', label: 'Self-Autonomous', icon: Zap },
  SUPERVISED:      { color: '#ea6b5e', label: 'Supervised',      icon: Eye },
  RESTRICTED:      { color: '#a8a29e', label: 'Restricted',      icon: Lock },
  PROBATION:       { color: '#78716c', label: 'Probation',       icon: ShieldAlert },
};

const TIME_RANGES = [
  { value: 24,   label: '24 hours' },
  { value: 168,  label: '7 days' },
  { value: 720,  label: '30 days' },
  { value: 2160, label: '90 days' },
];

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const card = {
  background: '#ffffff', border: '1px solid #e7e5e4', borderRadius: 12, padding: 24,
  boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
};

const h2Style = { fontSize: 18, fontWeight: 700, color: '#1c1917', margin: '0 0 4px 0' };
const h3Style = { fontSize: 15, fontWeight: 600, color: '#1c1917', margin: 0 };
const subtextStyle = { fontSize: 12, color: '#78716c', margin: 0 };
const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#57534e', marginBottom: 6, letterSpacing: '0.04em', textTransform: 'uppercase' };

const selectStyle = {
  padding: '7px 10px', borderRadius: 6, border: '1px solid #e7e5e4',
  background: '#ffffff', fontSize: 13, cursor: 'pointer', minWidth: 160,
};

const btnBase = {
  padding: '6px 14px', borderRadius: 6, border: 'none', fontSize: 12,
  fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
  transition: 'opacity 0.15s ease',
};
const btnPrimary = { ...btnBase, background: '#0d9488', color: '#fff' };
const btnSecondary = { ...btnBase, background: '#fafaf9', color: '#57534e', border: '1px solid #e7e5e4' };
const btnGhost = { ...btnBase, background: 'transparent', color: '#78716c', padding: '4px 8px' };

const pillStyle = (color, bg) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 10px',
  borderRadius: 20, fontSize: 11, fontWeight: 600, color, background: bg,
});

// ─────────────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────
const formatTimestamp = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const formatRelativeTime = (ts) => {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

const truncateHash = (hash) => {
  if (!hash) return '—';
  return hash.substring(0, 8) + '…' + hash.substring(hash.length - 6);
};

/**
 * Merge autonomy transitions + policy audit events into a single chronological timeline.
 * Each item gets a normalized shape: { type, timestamp, id, ...original }
 */
const mergeTimeline = (transitions, auditEvents) => {
  const items = [];

  (transitions || []).forEach((t) => {
    items.push({
      type: 'TRANSITION',
      timestamp: t.createdAt,
      id: t.id,
      data: t,
    });
  });

  (auditEvents || []).forEach((e) => {
    items.push({
      type: 'ENFORCEMENT',
      timestamp: e.createdAt,
      id: e.id,
      data: e,
    });
  });

  // Most recent first
  items.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  return items;
};

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

/** Score waterfall mini-chart — shows score trend with inline SVG */
const ScoreWaterfallChart = ({ snapshots, width = 540, height = 120 }) => {
  if (!snapshots || snapshots.length < 2) {
    return (
      <div style={{ ...card, padding: 20, textAlign: 'center', color: '#a8a29e', fontSize: 13 }}>
        Not enough data points for score trend visualization
      </div>
    );
  }

  const padding = { top: 16, right: 16, bottom: 28, left: 40 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const scores = snapshots.map((s) => Number(s.score ?? s.currentScore ?? 0));
  const dates = snapshots.map((s) => new Date(s.snapshotDate || s.createdAt));
  const minScore = Math.max(0, Math.min(...scores) - 5);
  const maxScore = Math.min(100, Math.max(...scores) + 5);
  const range = maxScore - minScore || 1;

  const toX = (i) => padding.left + (i / (scores.length - 1)) * chartW;
  const toY = (v) => padding.top + chartH - ((v - minScore) / range) * chartH;

  // Build path
  const pathParts = scores.map((s, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(s).toFixed(1)}`);
  const linePath = pathParts.join(' ');

  // Fill area under curve
  const areaPath = linePath
    + ` L${toX(scores.length - 1).toFixed(1)},${(padding.top + chartH).toFixed(1)}`
    + ` L${padding.left.toFixed(1)},${(padding.top + chartH).toFixed(1)} Z`;

  // Threshold lines
  const thresholds = [
    { value: 98, color: '#16a34a', label: '98' },
    { value: 90, color: '#d97706', label: '90' },
    { value: 75, color: '#dc2626', label: '75' },
  ].filter((t) => t.value >= minScore && t.value <= maxScore);

  // X-axis labels (first and last)
  const fmtDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      {/* Grid lines */}
      {thresholds.map((t) => (
        <g key={t.value}>
          <line
            x1={padding.left} y1={toY(t.value)} x2={width - padding.right} y2={toY(t.value)}
            stroke={t.color} strokeWidth={1} strokeDasharray="4 3" opacity={0.35}
          />
          <text x={padding.left - 4} y={toY(t.value) + 3} textAnchor="end" fontSize={9} fill={t.color} fontWeight={600}>
            {t.label}
          </text>
        </g>
      ))}

      {/* Area fill */}
      <path d={areaPath} fill="#0d9488" opacity={0.07} />

      {/* Line */}
      <path d={linePath} fill="none" stroke="#0d9488" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />

      {/* Data points */}
      {scores.map((s, i) => (
        <circle key={i} cx={toX(i)} cy={toY(s)} r={3} fill="#fff" stroke="#0d9488" strokeWidth={1.5} />
      ))}

      {/* X labels */}
      {dates.length > 0 && (
        <>
          <text x={padding.left} y={height - 4} fontSize={9} fill="#a8a29e">{fmtDate(dates[0])}</text>
          <text x={width - padding.right} y={height - 4} textAnchor="end" fontSize={9} fill="#a8a29e">
            {fmtDate(dates[dates.length - 1])}
          </text>
        </>
      )}
    </svg>
  );
};


/** Probation Tracker — visual countdown card */
const ProbationTracker = ({ profile }) => {
  if (!profile?.probationActive) return null;

  const start = profile.probationStartedAt ? new Date(profile.probationStartedAt) : null;
  const end = profile.probationExpiresAt ? new Date(profile.probationExpiresAt) : null;
  const now = new Date();

  let progressPct = 0;
  let remainingLabel = 'Indefinite';
  if (start && end) {
    const totalMs = end.getTime() - start.getTime();
    const elapsedMs = now.getTime() - start.getTime();
    progressPct = Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100));
    const remainMs = Math.max(0, end.getTime() - now.getTime());
    const remainDays = Math.floor(remainMs / 86400000);
    const remainHrs = Math.floor((remainMs % 86400000) / 3600000);
    remainingLabel = remainDays > 0 ? `${remainDays}d ${remainHrs}h remaining` : `${remainHrs}h remaining`;
  }

  return (
    <div style={{
      ...card, padding: 20, borderLeft: '4px solid #dc2626',
      background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <ShieldAlert size={18} color="#dc2626" />
        <h3 style={{ ...h3Style, color: '#dc2626' }}>Probation Active</h3>
        <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: '#dc2626' }}>
          {remainingLabel}
        </span>
      </div>

      {/* Progress bar */}
      <div style={{ background: '#fecaca', borderRadius: 6, height: 8, marginBottom: 12, overflow: 'hidden' }}>
        <div style={{
          width: `${progressPct}%`, height: '100%', borderRadius: 6,
          background: 'linear-gradient(90deg, #dc2626, #ef4444)',
          transition: 'width 0.6s ease',
        }} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#78716c' }}>
        <span>{start ? formatTimestamp(start) : '—'}</span>
        <span>{end ? formatTimestamp(end) : 'No expiry set'}</span>
      </div>

      {profile.probationReason && (
        <div style={{ marginTop: 12, padding: '10px 12px', background: '#fff5f5', borderRadius: 8, fontSize: 12, color: '#991b1b', lineHeight: 1.5 }}>
          <strong>Reason:</strong> {profile.probationReason}
        </div>
      )}

      {/* Recovery path */}
      <div style={{ marginTop: 14, padding: '10px 12px', background: '#f0fdf4', borderRadius: 8, fontSize: 12, color: '#166534', lineHeight: 1.5 }}>
        <strong>Recovery Path:</strong> Maintain trust score ≥ 75 with no BLOCK violations through probation period.
        Current score: <strong>{profile.currentScore != null ? Math.round(Number(profile.currentScore)) : 'N/A'}</strong>
      </div>
    </div>
  );
};


/** Hash verification badge */
const HashBadge = ({ hash, previousHash, verified }) => {
  if (!hash) return null;

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#78716c' }}>
      <Hash size={12} />
      <code style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#57534e', background: '#f5f5f4', padding: '1px 6px', borderRadius: 4 }}>
        {truncateHash(hash)}
      </code>
      {verified !== undefined && (
        <span style={pillStyle(verified ? '#16a34a' : '#dc2626', verified ? '#f0fdf4' : '#fef2f2')}>
          {verified ? <ShieldCheck size={10} /> : <ShieldAlert size={10} />}
          {verified ? 'Verified' : 'Broken'}
        </span>
      )}
    </div>
  );
};


/** Single timeline event card */
const TimelineEventCard = ({ item, isExpanded, onToggle }) => {
  const isTransition = item.type === 'TRANSITION';
  const d = item.data;

  if (isTransition) {
    const fromMeta = AUTONOMY_META[d.previousLevel] || { color: '#6b7280', label: d.previousLevel, icon: Shield };
    const toMeta = AUTONOMY_META[d.newLevel] || { color: '#6b7280', label: d.newLevel, icon: Shield };
    const isUpgrade = getAutonomyRank(d.newLevel) > getAutonomyRank(d.previousLevel);
    const FromIcon = fromMeta.icon;
    const ToIcon = toMeta.icon;

    return (
      <div style={{ ...card, padding: 0, overflow: 'hidden', borderLeft: `4px solid ${toMeta.color}` }}>
        {/* Header */}
        <div
          onClick={onToggle}
          style={{
            padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
            background: isExpanded ? '#fafaf9' : '#fff', transition: 'background 0.15s ease',
          }}
        >
          <div style={{
            width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: toMeta.color + '15',
          }}>
            {isUpgrade
              ? <TrendingUp size={16} color={toMeta.color} />
              : <TrendingDown size={16} color={toMeta.color} />}
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1c1917' }}>Autonomy Transition</span>
              <span style={pillStyle(fromMeta.color, fromMeta.color + '18')}>
                <FromIcon size={10} /> {fromMeta.label}
              </span>
              <ArrowRight size={12} color="#a8a29e" />
              <span style={pillStyle(toMeta.color, toMeta.color + '18')}>
                <ToIcon size={10} /> {toMeta.label}
              </span>
            </div>
            <div style={{ fontSize: 11, color: '#a8a29e', marginTop: 3 }}>
              {formatRelativeTime(d.createdAt)} · Score at transition: {d.scoreAtTransition != null ? Math.round(Number(d.scoreAtTransition)) : '—'}
            </div>
          </div>

          {isExpanded ? <ChevronUp size={16} color="#a8a29e" /> : <ChevronDown size={16} color="#a8a29e" />}
        </div>

        {/* Expanded detail */}
        {isExpanded && (
          <div style={{ padding: '0 18px 18px', borderTop: '1px solid #f5f5f4' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginTop: 14 }}>
              <DetailField label="Trigger Type" value={d.triggerType || '—'} />
              <DetailField label="Trigger Reason" value={d.triggerReason || d.reason || '—'} />
              <DetailField label="Score at Transition" value={d.scoreAtTransition != null ? Math.round(Number(d.scoreAtTransition)) : '—'} />
              <DetailField label="Compliance Score" value={d.complianceScoreAt != null ? Math.round(Number(d.complianceScoreAt)) : '—'} />
              <DetailField label="Violation Summary" value={d.violationSummary || 'None'} />
              <DetailField label="Decided By" value={d.decidedBy || 'System'} />
              {d.probationUntil && <DetailField label="Probation Until" value={formatTimestamp(d.probationUntil)} />}
              <DetailField label="Timestamp" value={formatTimestamp(d.createdAt)} />
            </div>

            {/* Hash chain */}
            <div style={{ marginTop: 14, padding: '10px 12px', background: '#fafaf9', borderRadius: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#57534e', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Audit Chain
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <HashBadge hash={d.transitionHash} previousHash={d.previousHash} />
                {d.previousHash && (
                  <div style={{ fontSize: 10, color: '#a8a29e' }}>
                    ← Previous: <code style={{ fontFamily: "'JetBrains Mono', monospace", background: '#f5f5f4', padding: '1px 4px', borderRadius: 3 }}>
                      {truncateHash(d.previousHash)}
                    </code>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── ENFORCEMENT EVENT ──
  const decision = DECISION_META[d.decision] || DECISION_META.AUDIT;
  const DecisionIcon = decision.icon;

  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden', borderLeft: `4px solid ${decision.color}` }}>
      {/* Header */}
      <div
        onClick={onToggle}
        style={{
          padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
          background: isExpanded ? '#fafaf9' : '#fff', transition: 'background 0.15s ease',
        }}
      >
        <div style={{
          width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: decision.bg,
        }}>
          <DecisionIcon size={16} color={decision.color} />
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1c1917' }}>
              {d.eventType || 'Policy Enforcement'}
            </span>
            <span style={pillStyle(decision.color, decision.bg)}>
              <DecisionIcon size={10} /> {decision.label}
            </span>
            {d.skillName && (
              <span style={{ fontSize: 11, color: '#78716c', fontStyle: 'italic' }}>
                {d.skillName}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#a8a29e', marginTop: 3 }}>
            {formatRelativeTime(d.createdAt)}
            {d.evaluationTimeMs != null && ` · ${d.evaluationTimeMs}ms`}
            {d.ruleId && ` · Rule ${truncateHash(d.ruleId)}`}
          </div>
        </div>

        {isExpanded ? <ChevronUp size={16} color="#a8a29e" /> : <ChevronDown size={16} color="#a8a29e" />}
      </div>

      {/* Expanded detail */}
      {isExpanded && (
        <div style={{ padding: '0 18px 18px', borderTop: '1px solid #f5f5f4' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginTop: 14 }}>
            <DetailField label="Event Type" value={d.eventType || '—'} />
            <DetailField label="Decision" value={decision.label} color={decision.color} />
            <DetailField label="Skill" value={d.skillName || '—'} />
            <DetailField label="Skill ID" value={d.skillId ? truncateHash(d.skillId) : '—'} mono />
            <DetailField label="Rule ID" value={d.ruleId ? truncateHash(d.ruleId) : '—'} mono />
            <DetailField label="Rule Version" value={d.ruleVersion ?? '—'} />
            <DetailField label="Evaluation Time" value={d.evaluationTimeMs != null ? `${d.evaluationTimeMs}ms` : '—'} />
            <DetailField label="Workflow ID" value={d.workflowExecutionId ? truncateHash(d.workflowExecutionId) : '—'} mono />
            <DetailField label="Token ID" value={d.tokenId || '—'} mono />
            <DetailField label="Timestamp" value={formatTimestamp(d.createdAt)} />
          </div>

          {/* Details JSON */}
          {d.details && Object.keys(d.details).length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#57534e', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Execution Details
              </div>
              <pre style={{
                background: '#1c1917', color: '#e7e5e4', padding: 14, borderRadius: 8,
                fontSize: 11, lineHeight: 1.6, overflow: 'auto', maxHeight: 200,
                fontFamily: "'JetBrains Mono', monospace", margin: 0,
              }}>
                {JSON.stringify(d.details, null, 2)}
              </pre>
            </div>
          )}

          {/* Hash chain */}
          <div style={{ marginTop: 14, padding: '10px 12px', background: '#fafaf9', borderRadius: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#57534e', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Audit Chain
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <HashBadge hash={d.eventHash} previousHash={d.previousEventHash} />
              {d.previousEventHash && (
                <div style={{ fontSize: 10, color: '#a8a29e' }}>
                  ← Previous: <code style={{ fontFamily: "'JetBrains Mono', monospace", background: '#f5f5f4', padding: '1px 4px', borderRadius: 3 }}>
                    {truncateHash(d.previousEventHash)}
                  </code>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


/** Small detail field for expanded cards */
const DetailField = ({ label, value, color, mono }) => (
  <div>
    <div style={{ fontSize: 10, fontWeight: 600, color: '#a8a29e', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
      {label}
    </div>
    <div style={{
      fontSize: 13, color: color || '#1c1917', fontWeight: 500,
      fontFamily: mono ? "'JetBrains Mono', monospace" : 'inherit',
      wordBreak: 'break-all',
    }}>
      {value}
    </div>
  </div>
);


/** Integrity verification panel */
const IntegrityPanel = ({ onVerify, verifyResult, verifying }) => (
  <div style={{ ...card, padding: 18, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
      <ShieldCheck size={20} color="#0d9488" />
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1c1917' }}>Audit Hash Chain Integrity</div>
        <div style={{ fontSize: 11, color: '#78716c' }}>Verify tamper-evident audit trail for all policy enforcement events</div>
      </div>
    </div>

    <button onClick={onVerify} disabled={verifying} style={{ ...btnPrimary, opacity: verifying ? 0.6 : 1 }}>
      {verifying ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <ShieldCheck size={12} />}
      {verifying ? 'Verifying…' : 'Verify Integrity'}
    </button>

    {verifyResult && (
      <div style={{
        width: '100%', marginTop: 8, padding: '10px 14px', borderRadius: 8,
        background: verifyResult.ok ? '#f0fdf4' : '#fef2f2',
        border: `1px solid ${verifyResult.ok ? '#bbf7d0' : '#fecaca'}`,
        fontSize: 12, color: verifyResult.ok ? '#166534' : '#991b1b',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        {verifyResult.ok
          ? <><CheckCircle size={14} /> Hash chain verified — {verifyResult.data?.eventsChecked ?? 0} events, no tampering detected</>
          : <><XCircle size={14} /> Verification failed: {verifyResult.error || verifyResult.data?.error || 'Unknown error'}</>}
      </div>
    )}
  </div>
);


// Autonomy level ranking for determining upgrade vs downgrade
function getAutonomyRank(level) {
  const ranks = { PROBATION: 0, RESTRICTED: 1, SUPERVISED: 2, SELF_AUTONOMOUS: 3 };
  return ranks[level] ?? -1;
}


// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const TrustTimelineTab = ({ profiles, loading: parentLoading, agentScope = null }) => {
  // ── State ──
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [timeRange, setTimeRange] = useState(720); // hours
  const [filterDecision, setFilterDecision] = useState('ALL');
  const [filterType, setFilterType] = useState('ALL'); // ALL | TRANSITION | ENFORCEMENT

  const [timeline, setTimeline] = useState([]);
  const [scoreTrends, setScoreTrends] = useState([]);
  const [agentProfile, setAgentProfile] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  // Fallback: fetch regular agents when no trust profiles exist yet
  const [fallbackAgents, setFallbackAgents] = useState([]);
  const [fallbackLoaded, setFallbackLoaded] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const sentinelRef = useRef(null);

  // Load regular agents as fallback when trust profiles are empty
  useEffect(() => {
    if (fallbackLoaded) return;
    if (profiles && profiles.length > 0) return; // trust profiles available, no need
    if (parentLoading) return; // still loading, wait

    let cancelled = false;
    const loadFallback = async () => {
      const res = await api.getAgents({});
      if (!cancelled && res.ok) {
        let agents = res.data || [];
        // Apply scope filter so the Timeline only shows relevant agents
        if (agentScope === 'external') {
          agents = agents.filter(a => a.isExternal);
        } else if (agentScope === 'native') {
          agents = agents.filter(a => !a.isExternal);
        }
        setFallbackAgents(agents);
      }
      if (!cancelled) setFallbackLoaded(true);
    };
    loadFallback();
    return () => { cancelled = true; };
  }, [profiles, parentLoading, fallbackLoaded, agentScope]);

  // ── Derived: merge trust profiles + fallback agents into a unified selector list ──
  const agentList = useMemo(() => {
    // Prefer trust profiles if available
    if (profiles && profiles.length > 0) {
      return profiles.map((p) => ({
        id: p.agentId,
        label: p.agentName || p.agentId,
        score: p.currentScore,
        level: p.currentAutonomyLevel,
        probation: p.probationActive,
        hasTrustProfile: true,
      })).sort((a, b) => a.label.localeCompare(b.label));
    }

    // Fallback: use regular agents (no trust data yet)
    if (fallbackAgents.length > 0) {
      return fallbackAgents
        .filter((a) => a.type !== 'system') // exclude system agents from selector
        .map((a) => ({
          id: a.id,
          label: a.name || a.id,
          score: null,
          level: null,
          probation: false,
          hasTrustProfile: false,
        })).sort((a, b) => a.label.localeCompare(b.label));
    }

    return [];
  }, [profiles, fallbackAgents]);

  // ── Load timeline data when agent or range changes ──
  const loadTimeline = useCallback(async () => {
    if (!selectedAgentId) return;
    setLoadingTimeline(true);
    setVerifyResult(null);

    const days = Math.ceil(timeRange / 24);

    const [historyRes, auditRes, trendsRes, profileRes] = await Promise.all([
      api.getAutonomyHistory(selectedAgentId, 0, 200),
      api.getPolicyAuditTrail({ agentId: selectedAgentId, hours: timeRange, page: 0, size: 200 }),
      api.getAgentScoreTrends(selectedAgentId, days),
      api.getTrustProfile(selectedAgentId).catch(() => ({ ok: false, data: null })),
    ]);

    const transitions = historyRes.ok ? (historyRes.data?.content ?? historyRes.data ?? []) : [];
    const auditEvents = auditRes.ok ? (auditRes.data?.content ?? auditRes.data ?? []) : [];
    const trends = trendsRes.ok ? (trendsRes.data || []) : [];
    const profile = profileRes.ok ? profileRes.data : null;

    setTimeline(mergeTimeline(transitions, auditEvents));
    setScoreTrends(trends);
    setAgentProfile(profile);
    setExpandedIds(new Set());
    setCurrentPage(1);
    setLoadingTimeline(false);
  }, [selectedAgentId, timeRange]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  // Auto-select first agent if list is available
  useEffect(() => {
    if (!selectedAgentId && agentList.length > 0) {
      setSelectedAgentId(agentList[0].id);
    }
  }, [agentList, selectedAgentId]);

  // ── Filter timeline (reset page on filter change) ──
  const filteredTimeline = useMemo(() => {
    let items = timeline;

    if (filterType !== 'ALL') {
      items = items.filter((i) => i.type === filterType);
    }

    if (filterDecision !== 'ALL') {
      items = items.filter((i) => {
        if (i.type === 'ENFORCEMENT') return i.data.decision === filterDecision;
        return true; // Transitions pass through decision filter
      });
    }

    return items;
  }, [timeline, filterType, filterDecision]);

  // Reset to page 1 when filters change
  useEffect(() => { setCurrentPage(1); }, [filterType, filterDecision]);

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(filteredTimeline.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const visibleTimeline = filteredTimeline.slice(startIdx, startIdx + pageSize);

  // ── Toggle expand ──
  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Verify integrity ──
  const handleVerify = async () => {
    setVerifying(true);
    const res = await api.verifyAuditIntegrity(timeRange);
    setVerifyResult(res);
    setVerifying(false);
  };

  // ── Stats summary ──
  const stats = useMemo(() => {
    const enforcements = timeline.filter((i) => i.type === 'ENFORCEMENT');
    const transitions = timeline.filter((i) => i.type === 'TRANSITION');
    return {
      total: timeline.length,
      enforcements: enforcements.length,
      transitions: transitions.length,
      blocks: enforcements.filter((e) => e.data.decision === 'BLOCKED').length,
      warns: enforcements.filter((e) => e.data.decision === 'WARNED').length,
      allowed: enforcements.filter((e) => e.data.decision === 'ALLOWED').length,
    };
  }, [timeline]);

  // ── Render ──
  if (parentLoading && !profiles?.length && !fallbackLoaded) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#78716c' }}>Loading agent profiles…</div>;
  }

  if (!agentList.length) {
    return (
      <div style={{ ...card, padding: 48, textAlign: 'center' }}>
        <Shield size={40} color="#d6d3d1" style={{ marginBottom: 12 }} />
        <div style={{ fontSize: 15, fontWeight: 600, color: '#57534e', marginBottom: 6 }}>No Agents Found</div>
        <div style={{ fontSize: 13, color: '#a8a29e' }}>
          Create agents in the Agent Lifecycle page, then run workflows through the policy enforcement pipeline to generate trust events.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* ── Agent selector + filters bar ── */}
      <div style={{
        ...card, padding: 16, marginBottom: 20,
        display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap',
      }}>
        <div style={{ flex: '1 1 200px', minWidth: 180 }}>
          <label style={labelStyle}>Agent</label>
          <select
            value={selectedAgentId}
            onChange={(e) => setSelectedAgentId(e.target.value)}
            style={{ ...selectStyle, width: '100%' }}
          >
            {agentList.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label}
                {a.hasTrustProfile
                  ? ` — Score ${a.score != null ? Math.round(Number(a.score)) : '?'}${a.probation ? ' ⚠ PROBATION' : ''}`
                  : ' — No trust data yet'}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Time Range</label>
          <select value={timeRange} onChange={(e) => setTimeRange(Number(e.target.value))} style={selectStyle}>
            {TIME_RANGES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Event Type</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={selectStyle}>
            <option value="ALL">All Events</option>
            <option value="TRANSITION">Autonomy Transitions</option>
            <option value="ENFORCEMENT">Policy Enforcements</option>
          </select>
        </div>

        <div>
          <label style={labelStyle}>Decision</label>
          <select value={filterDecision} onChange={(e) => setFilterDecision(e.target.value)} style={selectStyle}>
            <option value="ALL">All Decisions</option>
            <option value="BLOCKED">Blocked</option>
            <option value="WARNED">Warned</option>
            <option value="ALLOWED">Allowed</option>
            <option value="AUDIT">Audit</option>
            <option value="REDACTED">Redacted</option>
          </select>
        </div>

        <button
          onClick={loadTimeline}
          disabled={loadingTimeline}
          style={{ ...btnPrimary, alignSelf: 'flex-end', opacity: loadingTimeline ? 0.6 : 1 }}
        >
          <RefreshCw size={12} style={loadingTimeline ? { animation: 'spin 1s linear infinite' } : {}} />
          Refresh
        </button>
      </div>

      {/* ── Probation tracker ── */}
      {agentProfile && <div style={{ marginBottom: 20 }}><ProbationTracker profile={agentProfile} /></div>}

      {/* ── Score waterfall ── */}
      {scoreTrends.length >= 2 && (
        <div style={{ ...card, marginBottom: 20, padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <TrendingUp size={16} color="#0d9488" />
            <h3 style={h3Style}>Trust Score Trend</h3>
            {agentProfile && (
              <span style={{
                marginLeft: 'auto', fontSize: 22, fontWeight: 800,
                color: getScoreColor(agentProfile.currentScore),
              }}>
                {agentProfile.currentScore != null ? Math.round(Number(agentProfile.currentScore)) : '—'}
              </span>
            )}
          </div>
          <ScoreWaterfallChart snapshots={scoreTrends} width={Math.min(720, typeof window !== 'undefined' ? window.innerWidth - 120 : 600)} />

          {/* Threshold legend */}
          <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 10, color: '#a8a29e' }}>
            <span><span style={{ color: '#16a34a' }}>— 98</span> Self-Autonomous</span>
            <span><span style={{ color: '#d97706' }}>— 90</span> Supervised</span>
            <span><span style={{ color: '#dc2626' }}>— 75</span> Restricted</span>
          </div>
        </div>
      )}

      {/* ── Interactive horizontal timeline scrubber ── */}
      {filteredTimeline.length > 0 && (
        <div style={{ ...card, marginBottom: 20, padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <Activity size={16} color="#0d9488" />
            <h3 style={h3Style}>Event Timeline</h3>
            <span style={{ fontSize: 11, color: '#a8a29e', marginLeft: 4 }}>
              Drag the scrubber or click events to inspect
            </span>
          </div>
          <TimelineScrubber
            events={filteredTimeline}
            agentName={agentList.find((a) => a.id === selectedAgentId)?.label || selectedAgentId}
            agentCreatedAt={agentProfile?.createdAt}
          />
        </div>
      )}

      {/* ── Stats summary strip ── */}
      <div style={{
        display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap',
      }}>
        {[
          { label: 'Total Events', value: stats.total, color: '#1c1917' },
          { label: 'Transitions', value: stats.transitions, color: '#6366f1' },
          { label: 'Enforcements', value: stats.enforcements, color: '#0d9488' },
          { label: 'Blocked', value: stats.blocks, color: '#dc2626' },
          { label: 'Warned', value: stats.warns, color: '#d97706' },
          { label: 'Allowed', value: stats.allowed, color: '#16a34a' },
        ].map((s) => (
          <div key={s.label} style={{
            flex: '1 1 100px', padding: '12px 16px', background: '#fff',
            border: '1px solid #e7e5e4', borderRadius: 10, textAlign: 'center',
            borderTop: `3px solid ${s.color}`,
          }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 10, color: '#78716c', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Audit integrity verification ── */}
      <div style={{ marginBottom: 20 }}>
        <IntegrityPanel onVerify={handleVerify} verifyResult={verifyResult} verifying={verifying} />
      </div>

      {/* ── Timeline ── */}
      <div style={{ position: 'relative' }}>
        {loadingTimeline && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 12,
          }}>
            <Loader size={24} color="#0d9488" style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        )}

        {filteredTimeline.length === 0 && !loadingTimeline ? (
          <div style={{ ...card, padding: 48, textAlign: 'center' }}>
            <Clock size={32} color="#d6d3d1" style={{ marginBottom: 12 }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: '#57534e', marginBottom: 6 }}>No Events Found</div>
            <div style={{ fontSize: 13, color: '#a8a29e' }}>
              No trust events match the current filters for this time range.
            </div>
          </div>
        ) : (
          <>
            {/* Vertical timeline connector */}
            <div style={{ position: 'relative', paddingLeft: 24 }}>
              {/* Vertical line */}
              <div style={{
                position: 'absolute', left: 7, top: 0, bottom: 0, width: 2,
                background: 'linear-gradient(to bottom, #0d9488, #e7e5e4)',
                borderRadius: 2,
              }} />

              {visibleTimeline.map((item, idx) => (
                <div key={item.id} style={{ position: 'relative', marginBottom: 12 }}>
                  {/* Timeline dot */}
                  <div style={{
                    position: 'absolute', left: -20, top: 18, width: 10, height: 10,
                    borderRadius: '50%', border: '2px solid #fff',
                    background: item.type === 'TRANSITION'
                      ? (AUTONOMY_META[item.data.newLevel]?.color || '#6b7280')
                      : (DECISION_META[item.data.decision]?.color || '#6b7280'),
                    boxShadow: '0 0 0 2px #e7e5e4',
                    zIndex: 2,
                  }} />

                  <TimelineEventCard
                    item={item}
                    isExpanded={expandedIds.has(item.id)}
                    onToggle={() => toggleExpand(item.id)}
                  />
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {filteredTimeline.length > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '16px 0', gap: 16, flexWrap: 'wrap',
              }}>
                {/* Left: page size selector */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#78716c' }}>
                  <span>Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    style={{ ...selectStyle, minWidth: 60, padding: '4px 8px', fontSize: 12 }}
                  >
                    {[10, 20, 50, 100].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                  <span>per page</span>
                  <span style={{ marginLeft: 8, color: '#a8a29e' }}>
                    ({startIdx + 1}–{Math.min(startIdx + pageSize, filteredTimeline.length)} of {filteredTimeline.length})
                  </span>
                </div>

                {/* Center: page navigation */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={safePage <= 1}
                    style={{ ...btnGhost, opacity: safePage <= 1 ? 0.3 : 1 }}
                    title="First page"
                  >
                    ««
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage <= 1}
                    style={{ ...btnSecondary, opacity: safePage <= 1 ? 0.3 : 1, padding: '4px 10px' }}
                  >
                    ‹ Prev
                  </button>

                  {/* Page numbers */}
                  {(() => {
                    const pages = [];
                    const maxVisible = 5;
                    let start = Math.max(1, safePage - Math.floor(maxVisible / 2));
                    let end = Math.min(totalPages, start + maxVisible - 1);
                    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);

                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          style={{
                            ...btnBase,
                            padding: '4px 10px',
                            minWidth: 32,
                            justifyContent: 'center',
                            background: i === safePage ? '#0d9488' : '#fafaf9',
                            color: i === safePage ? '#fff' : '#57534e',
                            border: i === safePage ? 'none' : '1px solid #e7e5e4',
                            fontWeight: i === safePage ? 700 : 500,
                          }}
                        >
                          {i}
                        </button>
                      );
                    }
                    return pages;
                  })()}

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage >= totalPages}
                    style={{ ...btnSecondary, opacity: safePage >= totalPages ? 0.3 : 1, padding: '4px 10px' }}
                  >
                    Next ›
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={safePage >= totalPages}
                    style={{ ...btnGhost, opacity: safePage >= totalPages ? 0.3 : 1 }}
                    title="Last page"
                  >
                    »»
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Spin animation */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// Helper used in the chart
function getScoreColor(score) {
  /* The published band, through the shared service. This held three hex literals on a 90/75 ramp
   * — literals stay light-mode green on a dark background, and the ramp agreed with nothing. */
  return bandInk(publishedBand(score));
}

export default TrustTimelineTab;
